import { useId, useState, useRef, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import { kmGroupNumber } from './kmLabels';
import { NumberPadPopup } from '../americano/NumberPadPopup';
import { useT } from '../../i18n';
import { Dialog } from '../ui';
import { safeId } from '../ui/labelledControl';

const noSubscribe = () => () => {};

export interface KMScoreModalProps {
  /** The match whose result is being registered. */
  match: any;
  /** All pairs (resolves team labels). */
  pairs?: any[];
  /** Closes the modal. */
  onClose: () => void;
  /** Saves the updated match (score set / cleared / walkover). */
  onSave: (match: any) => void;
  /** Accent color for the summary + save button. */
  accentColor?: string;
}

/**
 * Score-entry modal for a KM match — set-by-set input with a tap-driven
 * NumberPad popup and smart opponent-side auto-fill, a set-standing summary,
 * and walkover (WO) shortcuts. Extracted verbatim from the original artifact
 * (legacy/App.jsx); markup/logic unchanged.
 */
export function KMScoreModal({
  match,
  pairs = [],
  onClose,
  onSave,
  accentColor = '#c01074',
}: KMScoreModalProps) {
  const p1 = match.team1?.pairId ? pairs.find((p) => p.id === match.team1.pairId) : null;
  const p2 = match.team2?.pairId ? pairs.find((p) => p.id === match.team2.pairId) : null;

  // The page-content wrapper (`.slide-up`) runs a CSS animation, which makes it a
  // STACKING CONTEXT. A modal rendered inside it is trapped there, so no z-index
  // can lift it above the app's sticky header (z-30) — the header painted over the
  // modal's top ~58px, hiding «Registrer resultat» with no way to scroll it back.
  // Portalling to <body> escapes that context. Guarded so server/static
  // rendering (and the render-contract tests) still return the markup inline.
  //
  // THE GUARD IS `useSyncExternalStore`, NOT A `mounted` STATE SET IN AN EFFECT
  // (#900). That version rendered the overlay inline first and portalled it on
  // the second render — a different element type, so React unmounted and
  // remounted it. Harmless for a plain div; for a `Dialog` it means opening it
  // twice: focus went in, back out to the opener, and in again, the scroll lock
  // was taken and released, and the second instance captured as its «opener» a
  // control inside the first one's panel, which was then removed. (Dialog's
  // way-back chain still finds the real opener — that is what it is for — but
  // only by luck of the order.) The server snapshot is `false` (inline, which
  // is what `renderToStaticMarkup` uses) and the client one `true` from the
  // first render, so the browser portals once and never remounts.
  const mounted = useSyncExternalStore(
    noSubscribe,
    () => true,
    () => false,
  );
  const t = useT();
  const uid = safeId('km-score', useId());
  const ids = {
    title: `${uid}-title`,
    sub: `${uid}-sub`,
    team1: `${uid}-team1`,
    team2: `${uid}-team2`,
    set: (i: number) => `${uid}-set-${i}`,
  };

  // Init state from existing score
  const existingSets = match.score?.sets || [];
  const [sets, setSets] = useState(
    existingSets.length > 0
      ? existingSets.map((s) => ({ t1: s.t1 ?? '', t2: s.t2 ?? '' }))
      : [
          { t1: '', t2: '' },
          { t1: '', t2: '' },
          { t1: '', t2: '' },
        ],
  );

  // Numpad state: which field has the popup open.
  // Shape: { setIdx, side: 't1'|'t2' } or null
  const [activePad, setActivePad] = useState(null);
  const padAnchorRef = useRef(null);

  // ── THE PAD FROM A KEYBOARD (#943) ─────────────────────────────────
  //
  // The score fields are `readOnly` on purpose: on a phone that keeps the
  // system keyboard away and the pad is the whole entry. But they opened the
  // pad on a CLICK only, so a keyboard could Tab to a field and do nothing with
  // it — a KM or series result could not be registered without a mouse.
  //
  // Now, the way the Americano row already behaves: FOCUS on a field opens its
  // pad (just shown — focus stays in the field, so Tab still walks the form),
  // and ENTER or SPACE moves focus into the pad, onto the field's current
  // value. Picking a number, Escape or the pad's ✕ brings focus back to the
  // field. Tabbing on to anything that is not the pad closes it, so it never
  // covers the control that has focus. The pointer path and what is saved are
  // untouched — `km-singlematch-dialogs.test.tsx` pins both.
  const [padFocus, setPadFocus] = useState(false);
  const quietFocus = useRef(false);
  const inPad = (el: Element | null) => !!el?.closest?.('[data-number-pad]');
  const openPad = (el: HTMLElement, setIdx: number, side: 't1' | 't2') => {
    padAnchorRef.current = el;
    setActivePad({ setIdx, side });
  };
  const closePad = () => {
    // Focus inside the pad (a keyboard pick, Escape or ✕) goes back to the
    // field it belongs to — without that focus opening the pad again.
    if (inPad(document.activeElement) && padAnchorRef.current) {
      quietFocus.current = true;
      padAnchorRef.current.focus();
      quietFocus.current = false;
    }
    setPadFocus(false);
    setActivePad(null);
  };
  const fieldKeys = (setIdx: number, side: 't1' | 't2') => ({
    onFocus: (e) => {
      if (quietFocus.current) return;
      openPad(e.currentTarget, setIdx, side);
    },
    onKeyDown: (e) => {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      e.preventDefault();
      openPad(e.currentTarget, setIdx, side);
      setPadFocus(true);
    },
    onBlur: (e) => {
      // Somewhere real that is not the pad: close it. A field's own focus
      // reopens it for that field in the same batch.
      if (e.relatedTarget && !inPad(e.relatedTarget)) {
        setPadFocus(false);
        setActivePad(null);
      }
    },
  });

  const fmt = match.matchFormat || 'bo3_super';
  const isBo3Super = fmt === 'bo3_super';
  // deviation (lint): `isBo3Full` is computed but unused in the monolith —
  // prefixed `_` to satisfy no-unused-vars; value preserved verbatim.
  const _isBo3Full = fmt === 'bo3_full';
  const isBo1 = fmt.startsWith('bo1');

  // Compute the winner based on sets
  const calcWinner = () => {
    let t1Sets = 0,
      t2Sets = 0;
    for (const s of sets) {
      const a = parseInt(s.t1, 10);
      const b = parseInt(s.t2, 10);
      if (isNaN(a) || isNaN(b)) continue;
      if (a > b) t1Sets++;
      else if (b > a) t2Sets++;
    }
    return { t1Sets, t2Sets };
  };

  const { t1Sets, t2Sets } = calcWinner();
  const isComplete = isBo1 ? t1Sets + t2Sets >= 1 : t1Sets >= 2 || t2Sets >= 2;

  const handleSave = () => {
    const validSets = sets
      .filter((s) => {
        const a = parseInt(s.t1, 10);
        const b = parseInt(s.t2, 10);
        return !isNaN(a) && !isNaN(b);
      })
      .map((s) => ({ t1: parseInt(s.t1, 10), t2: parseInt(s.t2, 10) }));

    if (validSets.length === 0) return;

    onSave({
      ...match,
      score: {
        t1: t1Sets,
        t2: t2Sets,
        sets: validSets,
      },
    });
  };

  const t1Name = p1 ? `${p1.p1Name} / ${p1.p2Name}` : 'Lag 1';
  const t2Name = p2 ? `${p2.p1Name} / ${p2.p2Name}` : 'Lag 2';

  // Walkover (WO): the team that did NOT show up loses. The opponent wins with
  // full margin (6-0, 6-0 — one set in bo1), as in official padel tournaments.
  // Marked with a walkover flag so it shows as WO and is kept out of ELO.
  const handleWalkover = (winnerSide) => {
    const setsCount = isBo1 ? 1 : 2;
    const woSets = Array.from({ length: setsCount }, () =>
      winnerSide === 't1' ? { t1: 6, t2: 0 } : { t1: 0, t2: 6 },
    );
    onSave({
      ...match,
      score: {
        t1: winnerSide === 't1' ? setsCount : 0,
        t2: winnerSide === 't2' ? setsCount : 0,
        sets: woSets,
        walkover: true,
        woWinner: winnerSide,
      },
    });
  };

  const overlay = (
    // A `Dialog` (#900): named by its title and described by the match line, Tab
    // kept inside, Escape closes as the backdrop does, focus given back. An open
    // number pad claims the first Escape (see `NumberPadPopup`), so Escape there
    // closes the pad and leaves the scores typed so far alone. Markup and what is
    // saved are unchanged — `km-singlematch-dialogs.test.tsx` pins the payloads.
    <Dialog
      labelledBy={ids.title}
      describedBy={ids.sub}
      onClose={onClose}
      showClose={false}
      backdropClassName="fixed inset-0 z-50 flex items-center justify-center p-4 bg-ink/40 backdrop-blur-sm"
      // The panel is tall (up to 3 sets + set summary + walkover + actions). The
      // overlay centres it, so without a height cap an overflowing panel is clipped
      // at BOTH ends — the header and, worse, the «Lagre» button become unreachable
      // (flex centring cannot be scrolled back into view). Cap it to the viewport
      // minus the overlay's p-4 (2rem total) and scroll inside instead.
      className="bg-white rounded-xl shadow-xl w-full max-w-md p-5 overflow-y-auto"
      style={{ maxHeight: 'calc(100dvh - 2rem)' }}
    >
      <div className="flex items-start justify-between mb-4">
        <div>
          <h2 id={ids.title} className="font-bold text-lg text-ink">
            Registrer resultat
          </h2>
          <p id={ids.sub} className="text-xs text-ink-2 mt-0.5">
            {match.phase === 'group'
              ? `Gruppe ${kmGroupNumber(match.groupName)} · Runde ${match.roundIndex}`
              : match.phase === 'league'
                ? `Runde ${match.round}`
                : typeof match.round === 'string'
                  ? match.round.toUpperCase()
                  : match.round != null
                    ? `Runde ${match.round}`
                    : ''}
            {match.startTime && ` · ${match.startTime}`}
            {match.court && ` · Bane ${match.court}`}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label={t('ui.dialog.close')}
          className="text-ink-3 hover:text-ink p-1"
        >
          ✕
        </button>
      </div>

      {/* Team headings — aligned with the score fields below (flex with the same gap) */}
      <div className="flex items-center gap-2 mb-3 mt-3 px-2.5">
        <div
          id={ids.team1}
          className="flex-1 min-w-0 text-center text-xs font-bold text-ink truncate"
        >
          {t1Name}
        </div>
        <div className="px-1 text-xs flex-shrink-0 invisible">–</div>
        <div
          id={ids.team2}
          className="flex-1 min-w-0 text-center text-xs font-bold text-ink truncate"
        >
          {t2Name}
        </div>
      </div>

      {/* Set input with clickable fields + numpad popup (same style as Americano).

            Tennis rules for smart auto-fill:
            - Regular set: winner needs 6 (with a 2-point gap), or 7-5/7-6
            - Valid final scores: 6-0..6-4, 7-5, 7-6 (or mirrored)
            - Super-tiebreak: winner = 10, gap 2 (10-0..10-8, 12-10, etc.)

            UX: the user clicks a field → popup shows number options →
            click a number → the value is set + the other side auto-fills + focus
            moves to the next set.
        */}
      <div className="space-y-3">
        {sets.map((s, i) => {
          const isSuperTB = isBo3Super && i === 2;
          // deviation (lint): `maxVal` is computed but unused in this map body
          // in the monolith — prefixed `_` to satisfy no-unused-vars.
          const _maxVal = isSuperTB ? 15 : 7;

          // Smart-fill: return a suggestion for the other side based on the chosen value
          const suggestOtherSide = (myValue) => {
            if (myValue === '') return '';
            const v = parseInt(myValue, 10);
            if (isNaN(v)) return '';

            if (isSuperTB) {
              if (v <= 8) return 10;
              if (v === 9) return 11;
              if (v === 10) return 8;
              if (v >= 11) return v - 2;
              return '';
            }

            if (v <= 4) return 6;
            if (v === 5) return 7;
            if (v === 6) return 4;
            if (v === 7) return 5;
            return '';
          };

          // deviation (lint): `setVal` is defined but unused in the monolith
          // (score entry goes through the NumberPadPopup) — prefixed `_`.
          const _setVal = (sideKey, value) => {
            const otherKey = sideKey === 't1' ? 't2' : 't1';
            setSets((prev) =>
              prev.map((x, j) => {
                if (j !== i) return x;
                const next = { ...x, [sideKey]: value };
                if (value !== '' && (x[otherKey] === '' || x[otherKey] === null)) {
                  const suggested = suggestOtherSide(value);
                  if (suggested !== '') next[otherKey] = String(suggested);
                }
                return next;
              }),
            );
          };

          const isActive1 = activePad?.setIdx === i && activePad?.side === 't1';
          const isActive2 = activePad?.setIdx === i && activePad?.side === 't2';

          return (
            <div key={i} className="bg-bg-soft/40 rounded-lg p-2.5">
              <div
                id={ids.set(i)}
                className="text-[10px] font-mono uppercase tracking-wider text-ink-3 mb-2 font-semibold"
              >
                {isSuperTB ? 'Super-tiebreak (til 10)' : `Sett ${i + 1}`}
              </div>
              <div className="flex items-center gap-2">
                {/* Team 1 score field — left side, under team 1's name */}
                <input
                  type="text"
                  inputMode="numeric"
                  readOnly
                  value={s.t1}
                  // Named by the set and the team it scores, both already on
                  // screen — «Sett 1 Ola / Per» — rather than by nothing.
                  aria-labelledby={`${ids.set(i)} ${ids.team1}`}
                  onClick={(e) => {
                    padAnchorRef.current = e.target;
                    setActivePad({ setIdx: i, side: 't1' });
                  }}
                  {...fieldKeys(i, 't1')}
                  placeholder="—"
                  className={`flex-1 min-w-0 px-2 py-2 border-2 rounded-lg text-center font-mono text-xl font-bold cursor-pointer focus-ring transition-colors ${
                    isActive1
                      ? 'border-ink bg-white'
                      : s.t1 !== ''
                        ? 'border-line-strong bg-white text-ink'
                        : 'border-line-strong bg-paper text-ink-3 hover:border-ink'
                  }`}
                />
                <div className="text-ink-3 font-mono text-sm flex-shrink-0 px-1">–</div>
                {/* Team 2 score field — right side, under team 2's name */}
                <input
                  type="text"
                  inputMode="numeric"
                  readOnly
                  value={s.t2}
                  aria-labelledby={`${ids.set(i)} ${ids.team2}`}
                  onClick={(e) => {
                    padAnchorRef.current = e.target;
                    setActivePad({ setIdx: i, side: 't2' });
                  }}
                  {...fieldKeys(i, 't2')}
                  placeholder="—"
                  className={`flex-1 min-w-0 px-2 py-2 border-2 rounded-lg text-center font-mono text-xl font-bold cursor-pointer focus-ring transition-colors ${
                    isActive2
                      ? 'border-ink bg-white'
                      : s.t2 !== ''
                        ? 'border-line-strong bg-white text-ink'
                        : 'border-line-strong bg-paper text-ink-3 hover:border-ink'
                  }`}
                />
              </div>
            </div>
          );
        })}
      </div>

      {/* Numpad popup when a field is active */}
      {activePad &&
        (() => {
          const setIdx = activePad.setIdx;
          const side = activePad.side;
          const isSuperTB = isBo3Super && setIdx === 2;
          const maxVal = isSuperTB ? 15 : 7;

          const suggestOtherSide = (v) => {
            if (isSuperTB) {
              if (v <= 8) return 10;
              if (v === 9) return 11;
              if (v === 10) return 8;
              if (v >= 11) return v - 2;
            } else {
              if (v <= 4) return 6;
              if (v === 5) return 7;
              if (v === 6) return 4;
              if (v === 7) return 5;
            }
            return '';
          };

          return (
            <NumberPadPopup
              anchorRef={padAnchorRef}
              maxValue={maxVal}
              focusInside={padFocus}
              value={sets[setIdx]?.[side]}
              closeOnFocusLeave
              onPick={(n) => {
                // A pick made from the keyboard (focus is on the pad's button).
                const fromKeyboard = inPad(document.activeElement);
                const otherSide = side === 't1' ? 't2' : 't1';
                setSets((prev) =>
                  prev.map((x, j) => {
                    if (j !== setIdx) return x;
                    const next = { ...x, [side]: String(n) };
                    if (x[otherSide] === '' || x[otherSide] === null) {
                      const suggested = suggestOtherSide(n);
                      if (suggested !== '') next[otherSide] = String(suggested);
                    }
                    return next;
                  }),
                );
                // If the other side was empty and we auto-filled it: close the popup.
                // Otherwise: jump to the next set's t1 field if it is empty.
                setTimeout(() => {
                  // Check whether more sets are missing values
                  setSets((curr) => {
                    let nextPadFound = false;
                    for (let k = 0; k < curr.length; k++) {
                      if (k === setIdx) continue;
                      if (curr[k].t1 === '' || curr[k].t2 === '') {
                        nextPadFound = true;
                        break;
                      }
                    }
                    if (!nextPadFound) setActivePad(null);
                    else setActivePad(null); // close regardless, the user picks the next one
                    return curr;
                  });
                }, 30);
                setPadFocus(false);
                if (fromKeyboard) closePad();
              }}
              onClose={closePad}
            />
          );
        })()}

      {/* Score summary */}
      <div
        className="mt-4 p-3 rounded-md text-center"
        style={{ background: accentColor + '0f', border: `1px solid ${accentColor}30` }}
      >
        <div className="text-[10px] font-mono uppercase tracking-wider text-ink-3">
          Sett-stilling
        </div>
        <div className="text-2xl font-bold text-ink mt-0.5">
          {t1Sets} – {t2Sets}
        </div>
        {isComplete && (
          <div className="text-xs font-bold mt-1" style={{ color: accentColor }}>
            {t1Sets > t2Sets ? '🏆 ' + t1Name + ' vinner' : '🏆 ' + t2Name + ' vinner'}
          </div>
        )}
      </div>

      {/* Walkover (WO) — register that a team did not show up. The opponent wins. */}
      {p1 && p2 && (
        <div className="mt-4 pt-3 border-t border-ink/10">
          <div className="text-[11px] font-mono uppercase tracking-wider text-ink-3 mb-2">
            Walkover (WO) — lag møtte ikke
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <button
              type="button"
              onClick={() => handleWalkover('t2')}
              className="flex-1 min-w-0 px-3 py-2 rounded-lg text-xs font-semibold border border-line-strong text-ink-2 hover:border-ink hover:bg-bg-soft/60 transition-colors text-left"
              title="Lag 1 møtte ikke — Lag 2 vinner på walkover (6-0, 6-0)"
            >
              <span className="block font-bold text-ink truncate">{t1Name}</span>
              <span className="text-ink-3">møtte ikke → motstander vinner WO</span>
            </button>
            <button
              type="button"
              onClick={() => handleWalkover('t1')}
              className="flex-1 min-w-0 px-3 py-2 rounded-lg text-xs font-semibold border border-line-strong text-ink-2 hover:border-ink hover:bg-bg-soft/60 transition-colors text-left"
              title="Lag 2 møtte ikke — Lag 1 vinner på walkover (6-0, 6-0)"
            >
              <span className="block font-bold text-ink truncate">{t2Name}</span>
              <span className="text-ink-3">møtte ikke → motstander vinner WO</span>
            </button>
          </div>
        </div>
      )}

      <div className="flex items-center justify-end gap-2 mt-5 pt-4 border-t border-ink/10">
        {match.score && (
          <button
            type="button"
            onClick={() => onSave({ ...match, score: null })}
            className="px-3 py-2 text-xs text-rose hover:bg-rose-soft rounded mr-auto"
          >
            Slett resultat
          </button>
        )}
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 rounded text-sm text-ink-2 hover:bg-bg-soft"
        >
          Avbryt
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={!isComplete}
          className="px-4 py-2 rounded text-sm font-bold disabled:opacity-40"
          style={{ background: accentColor, color: '#fff' }}
        >
          Lagre
        </button>
      </div>
    </Dialog>
  );

  return mounted ? createPortal(overlay, document.body) : overlay;
}
